import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, EMPTY, forkJoin, map, Subject, switchMap, takeUntil } from 'rxjs';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { TranslateModule } from '@ngx-translate/core';
import { HttpService } from '../../../services/http.service';
import { AppointmentStepperService } from '../../../services/appointment-stepper.service';
import { RelatedProfileDTO, UserAccountDTO, UsersAccountDTO } from '../../../classes/user-account.dto';
import { InfoBoxComponent } from '../../utility/info-box/info-box.component';

@Component({
    selector: 'app-profile-selection-input',
    imports: [ReactiveFormsModule, NzGridModule, TranslateModule, InfoBoxComponent],
    templateUrl: './profile-selection-input.component.html',
    styleUrl: './profile-selection-input.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ProfileSelectionInputComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Output() profileSelectionInputEmitter: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Input() typeAppointment: string = '';
  formData: any = {
    form: {
      nameId: 'name',
      surnameId: 'surname',
      taxcodeId: 'taxId',
      addressId: 'address',
      provinceId: 'birthDistrict',
      countryId: 'birthCountry',
      cityId: 'birthCity',
      zipcodeId: 'postalCode',
      nameDoctorId: 'name',
      surnameDoctorId: 'surname',
      addressDoctorId: 'address',
      phoneDoctorId: 'phone',
      parentGradeId: 'parentGrade',
    },
  };
  inputsForm?: FormGroup;
  inputConfigs: { [key: string]: any } = {};
  countriesData?: Array<{ text: string; value: string }>;
  districtData?: Array<{ text: string; value: string }>;
  citiesData?: Array<{ text: string; value: string }>;
  userData: UsersAccountDTO | RelatedProfileDTO | null = null;
  kinshipData?: Array<{ text: string; value: string }>;
  isParentalGradeAble: boolean = true;

  constructor(
    private fb: FormBuilder,
    private http: HttpService,
    private appointmentService: AppointmentStepperService,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.getKinshipData();
    this.createForm();
    this.getChosenUser();
    this.emitProfileSelectionInputForm();
  }

  private createForm(): void {
    const { form } = this.formData;
    this.inputConfigs = {
      nameInput: this.createInputControl(),
      surnameInput: this.createInputControl(),
      taxcodeInput: this.createInputControl(),
      countryInput: this.createInputControl(),
      provinceInput: this.createInputControl(),
      cityInput: this.createInputControl(),
      zipcodeInput: this.createInputControl(),
      addressInput: this.createInputControl(),
      nameDoctorInput: this.createInputControl(),
      surnameDoctorInput: this.createInputControl(),
      addressDoctorInput: this.createInputControl(),
      phoneDoctorInput: this.createInputControl(),
      parentGradeInput: this.createInputControl(),
    };

    this.inputsForm = this.fb.group({
      personalData: this.fb.group({
        [form.nameId]: this.inputConfigs['nameInput'].control,
        [form.surnameId]: this.inputConfigs['surnameInput'].control,
        [form.taxcodeId]: this.inputConfigs['taxcodeInput'].control,
        [form.countryId]: this.inputConfigs['countryInput'].control,
        [form.provinceId]: this.inputConfigs['provinceInput'].control,
        [form.cityId]: this.inputConfigs['cityInput'].control,
        [form.zipcodeId]: this.inputConfigs['zipcodeInput'].control,
        [form.addressId]: this.inputConfigs['addressInput'].control,
        [form.parentGradeId]: this.inputConfigs['parentGradeInput'].control,
      }),
      medicalData: this.fb.group({
        [form.nameDoctorId]: this.inputConfigs['nameDoctorInput'].control,
        [form.surnameDoctorId]: this.inputConfigs['surnameDoctorInput'].control,
        [form.addressDoctorId]: this.inputConfigs['addressDoctorInput'].control,
        [form.phoneDoctorId]: this.inputConfigs['phoneDoctorInput'].control,
      }),
    });
  }

  private getChosenUser(): void {
    this.userData = this.appointmentService.getMainUser();
    forkJoin({
      userData: this.http.getAccountdDataNoConnectionValidations(this.userData?.individualId!),
      doctorData: this.http.getDoctorDetails(this.userData?.individualId!),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ userData, doctorData }) => {
        const user = userData.customer;
        const mainUser = JSON.parse(localStorage.getItem('userData')!) as UserAccountDTO;
        const controlNames = this.formData.form;
        this.inputsForm?.get('personalData')?.get(controlNames.nameId)?.patchValue(user?.name);
        this.inputsForm?.get('personalData')?.get(controlNames.surnameId)?.patchValue(user?.surname);
        this.inputsForm?.get('personalData')?.get(controlNames.taxcodeId)?.patchValue(user?.taxId);
        this.inputsForm?.get('personalData')?.get(controlNames.zipcodeId)?.patchValue(user?.postalCode);
        this.inputsForm?.get('personalData')?.get(controlNames.addressId)?.patchValue(user?.address);
        this.fillUserBirthData(user);
        this.inputsForm?.get('medicalData')?.get(controlNames.nameDoctorId)?.patchValue(doctorData?.name);
        this.inputsForm?.get('medicalData')?.get(controlNames.surnameDoctorId)?.patchValue(doctorData?.surname);
        this.inputsForm?.get('medicalData')?.get(controlNames.addressDoctorId)?.patchValue(doctorData?.address);
        this.inputsForm?.get('medicalData')?.get(controlNames.phoneDoctorId)?.patchValue(doctorData?.phone);
        if (user.individualId === mainUser.individualId) {
          this.inputsForm?.get('personalData')?.get(controlNames.parentGradeId)?.clearValidators();
          this.inputsForm?.get('personalData')?.get(controlNames.parentGradeId)?.updateValueAndValidity({ emitEvent: false });
          this.isParentalGradeAble = false;
        }
        this.cdr.detectChanges();
      });
  }

  private createInputControl(): any {
    return {
      control: new FormControl(null, [Validators.required]),
    };
  }

  private emitProfileSelectionInputForm(): void {
    if (this.inputsForm) {
      this.inputsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((_): void => {
        this.profileSelectionInputEmitter.emit(this.inputsForm);
      });
    }
  }

  private getCountriesData(): void {
    this.http
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((country) => {
          return country.map((country: any) => ({ text: country.description, value: country.code }));
        })
      )
      .subscribe((res: any): void => {
        this.countriesData = res;
        this.placeListeners();
      });
  }

  private placeListeners(): void {
    if (this.inputsForm) {
      this.inputsForm
        .get('personalData')
        ?.get(this.formData.form.countryId)
        ?.valueChanges.pipe(
          switchMap((personalData) => this.http.getDistrict(personalData.value)),
          map((districts: any) => districts.map((district: any) => ({ text: district.description, value: district.code }))),
          takeUntil(this.destroy$),
          catchError((err) => {
            this.districtData = [];
            console.error(err);
            return EMPTY;
          })
        )
        .subscribe((res: any) => {
          this.cdr.detectChanges();
        });

      this.inputsForm
        .get('personalData')
        ?.get(this.formData.form.provinceId)
        ?.valueChanges.pipe(
          switchMap((personalData) => this.http.getProvince(personalData?.value)),
          map((cities: any) => cities.map((city: any) => ({ text: city.description, value: city.code }))),
          takeUntil(this.destroy$),
          catchError((err) => {
            this.citiesData = [];
            console.error(err);
            return EMPTY;
          })
        )
        .subscribe((res: any) => {
          this.citiesData = res;
          this.cdr.detectChanges();
        });
    }
  }

  private getKinshipData(): any {
    this.http
      .getKinship()
      .pipe(takeUntil(this.destroy$))
      .subscribe((kinshipList: Array<{ id: string; name: string }>) => {
        this.kinshipData = kinshipList.map((kinship: { id: string; name: string }) => ({ text: kinship.name, value: kinship.id }));
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fillUserBirthData(user: any) {
    this.http
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((country) => {
          return country.map((country: any) => ({ text: country.description, value: country.code }));
        })
      )
      .subscribe((res: any): void => {
        this.countriesData = res;
        const countryValue = this.countriesData!.find((c) => c.value === user.birthCountry.code);
        this.inputsForm?.get('personalData')?.get(this.formData.form.countryId)?.patchValue(countryValue);
        this.http
          .getDistrict(countryValue?.value!)
          .pipe(
            takeUntil(this.destroy$),
            map((districts) => {
              return districts.map((district: any) => ({ text: district.description, value: district.code }));
            })
          )
          .subscribe((res: any): void => {
            this.districtData = res;
            const districtValue = this.districtData!.find((c) => c.value === user.birthDistrict.code);
            this.inputsForm?.get('personalData')?.get(this.formData.form.provinceId)?.patchValue(districtValue);
            this.http
              .getProvince(districtValue?.value!)
              .pipe(
                takeUntil(this.destroy$),
                map((cities) => {
                  return cities.map((city: any) => ({ text: city.description, value: city.code }));
                })
              )
              .subscribe((res: any): void => {
                this.citiesData = res;
                const cityValue = this.citiesData!.find((c) => c.value === user.birthCity.code);
                this.inputsForm?.get('personalData')?.get(this.formData.form.cityId)?.setValue(cityValue);
              });
          });
      });
  }
}
